import { AddIcon } from '@chakra-ui/icons';
import {
    Alert,
    AlertIcon,
    Box,
    IconButton,
    Table,
    TableContainer,
    Tbody,
    Td,
    Text,
    Th,
    Thead,
    Tooltip,
    Tr,
} from '@chakra-ui/react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useRef, useState } from 'react';
import { TbTrash } from 'react-icons/tb';
import { useSelector } from 'react-redux';

import { Card, CardBody } from '@/components/v4';
import {
    getDuplicateRowIds,
    initTableState,
    isRowComplete,
} from '../constants';
import {
    EditableItemCodeCell,
    EditableQuantityCell,
    EditableUnitPriceCell,
    EditableDescriptionCell,
} from './EditableCells';

function AddInvoiceLineTable({
    aggregateTable,
    updateAggregateTable,
    setCanSubmit,
    existingLines = [],
}) {
    const [focusedRow, setFocusedRow] = useState({ row: -1, column: null });
    const isAddingRowRef = useRef(false);
    const columnHelper = createColumnHelper();
    const { darkMode } = useSelector((state) => state.settings);

    const duplicateRowIds = useMemo(
        () => getDuplicateRowIds(aggregateTable, existingLines),
        [aggregateTable, existingLines],
    );

    const columns = [
        columnHelper.accessor('quantity', {
            header: () => (
                <Text>
                    Quantity <span style={{ color: '#e53e3e' }}>*</span>
                </Text>
            ),
            cell: EditableQuantityCell,
            size: 120,
        }),
        columnHelper.accessor('unitPrice', {
            header: () => (
                <Text>
                    Unit Price <span style={{ color: '#e53e3e' }}>*</span>
                </Text>
            ),
            cell: EditableUnitPriceCell,
            size: 120,
        }),
        columnHelper.accessor('description', {
            header: () => (
                <Text>
                    Description <span style={{ color: '#e53e3e' }}>*</span>
                </Text>
            ),
            cell: EditableDescriptionCell,
            size: 280,
        }),
        columnHelper.accessor('itemCode', {
            header: () => (
                <Text>
                    Item Code <span style={{ color: '#e53e3e' }}>*</span>
                </Text>
            ),
            cell: EditableItemCodeCell,
            size: 150,
        }),
        columnHelper.display({
            id: 'actions',
            header: '',
            size: 80,
            cell: ({ row, table }) => {
                const rowCount = table.options.data.length;
                const rowData = row.original;
                const isComplete = isRowComplete(rowData);
                const isFirstRow = row.index === 0;
                const showDelete = rowCount > 1;
                const showAdd = isComplete && isFirstRow;

                return (
                    <Box display="flex" gap={1} alignItems="center">
                        {showDelete ? (
                            <Tooltip label="Delete row" hasArrow>
                                <IconButton
                                    size="sm"
                                    rounded="full"
                                    variant="ghost"
                                    color={darkMode ? 'white' : 'gray.500'}
                                    icon={<TbTrash />}
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        const newIndex =
                                            row.index > 0 ? row.index - 1 : 0;
                                        table.options.meta.deleteRow(row.index);
                                        table.options.meta.setFocusedRow(
                                            newIndex,
                                            'quantity',
                                        );
                                    }}
                                    _hover={{
                                        bg: darkMode
                                            ? 'dark.hoverGray'
                                            : 'gray.100',
                                        color: 'red.500',
                                    }}
                                    _focus={{
                                        boxShadow: 'none',
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Box w="32px" aria-label="action placeholder" />
                        )}

                        {showAdd ? (
                            <Tooltip label="Add new row" hasArrow>
                                <IconButton
                                    size="sm"
                                    rounded="full"
                                    variant="ghost"
                                    icon={
                                        <AddIcon
                                            color={
                                                darkMode ? 'white' : 'gray.500'
                                            }
                                            boxSize="12px"
                                        />
                                    }
                                    onMouseDown={(e) => e.preventDefault()}
                                    onClick={() => {
                                        table.options.meta.addNewRow();
                                    }}
                                    _hover={{
                                        bg: darkMode
                                            ? 'dark.hoverGray'
                                            : 'gray.100',
                                        color: 'brand.500',
                                    }}
                                    _focus={{
                                        boxShadow: 'none',
                                    }}
                                />
                            </Tooltip>
                        ) : (
                            <Box w="32px" aria-label="action placeholder" />
                        )}
                    </Box>
                );
            },
        }),
    ];

    // Check if form can be submitted (all rows complete, no empty rows except if only 1 row)
    useEffect(() => {
        const allRowsComplete = aggregateTable.every((row) =>
            isRowComplete(row),
        );
        const hasAtLeastOneRow = aggregateTable.length >= 1;
        const firstRowComplete = aggregateTable[0]
            ? isRowComplete(aggregateTable[0])
            : false;

        // Can submit if: has at least one row AND first row is complete AND
        // all rows are complete AND no description duplicates another row or
        // an existing invoice line (#1941 — backend silently rejects these).
        setCanSubmit(
            hasAtLeastOneRow &&
                firstRowComplete &&
                allRowsComplete &&
                duplicateRowIds.size === 0,
        );
    }, [aggregateTable, duplicateRowIds, setCanSubmit]);

    const table = useReactTable({
        columns,
        data: aggregateTable,
        getCoreRowModel: getCoreRowModel(),
        meta: {
            focusedRow,
            setFocusedRow: (rowIndex, columnId) => {
                setFocusedRow({ row: rowIndex, column: columnId });
            },
            isAddingRowRef,
            duplicateRowIds,
            updateData: (rowIndex, columnId, value, addRow = false) => {
                if (addRow) {
                    isAddingRowRef.current = true;
                    setTimeout(() => {
                        isAddingRowRef.current = false;
                    }, 0);
                }
                updateAggregateTable((prevTable) => {
                    let newTable = [...prevTable];
                    newTable[rowIndex] = {
                        ...newTable[rowIndex],
                        [columnId]: value,
                    };

                    if (addRow) {
                        // Add new empty row at the top
                        newTable.unshift({
                            id: 0,
                            itemCode: '',
                            quantity: '',
                            unitPrice: '',
                            description: '',
                        });

                        // Re-index all rows
                        newTable = newTable.map((row, idx) => ({
                            ...row,
                            id: idx,
                        }));
                    }

                    return newTable;
                });
            },
            deleteRow: (rowIndex) => {
                updateAggregateTable((prevTable) => {
                    const newTable = prevTable.filter(
                        (_, index) => index !== rowIndex,
                    );
                    // Ensure we always have at least one row
                    if (newTable.length === 0) {
                        return initTableState;
                    }
                    // Re-index the remaining rows
                    return newTable.map((row, index) => ({
                        ...row,
                        id: index,
                    }));
                });
            },
            addNewRow: () => {
                isAddingRowRef.current = true;
                setTimeout(() => {
                    isAddingRowRef.current = false;
                }, 0);
                updateAggregateTable((prevTable) => {
                    let newTable = [
                        {
                            id: 0,
                            itemCode: '',
                            quantity: '',
                            unitPrice: '',
                            description: '',
                        },
                        ...prevTable,
                    ];
                    // Re-index all rows
                    return newTable.map((row, idx) => ({
                        ...row,
                        id: idx,
                    }));
                });
                setFocusedRow({ row: 0, column: 'quantity' });
            },
            clearAllRows: () => {
                updateAggregateTable(() => initTableState);
                setFocusedRow({ row: 0, column: 'quantity' });
            },
        },
    });

    return (
        <Box>
            {duplicateRowIds.size > 0 && (
                <Alert status="error" maxW="895px" mb={3}>
                    <AlertIcon />
                    Duplicate description found — invoice lines must have a
                    unique description. Rows with a repeated description are
                    highlighted below.
                </Alert>
            )}
            <Card
                bg={darkMode ? 'dark.tcap' : '#FFF'}
                maxW="895px"
                shadow="none"
                mt={4}>
                <CardBody p={0}>
                    <TableContainer
                        width="full"
                        maxW="full"
                        maxHeight="400px"
                        overflowY="auto"
                        sx={{
                            '&': {
                                scrollbarWidth: 'thin',
                            },
                        }}>
                        <Table size="sm" variant="unstyled">
                            <Thead
                                position="sticky"
                                top={0}
                                zIndex={10}
                                height="48px"
                                background={darkMode ? 'dark.tcap' : '#FFF'}>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <Tr
                                        key={headerGroup.id}
                                        sx={{
                                            '& th': {
                                                backgroundColor: darkMode
                                                    ? 'var(--chakra-colors-dark-tcap)!important'
                                                    : '#FFF!important',
                                            },
                                        }}>
                                        {headerGroup.headers.map(
                                            (header, headerIndex) => {
                                                const isFirst =
                                                    headerIndex === 0;
                                                const isLast =
                                                    headerIndex ===
                                                    headerGroup.headers.length -
                                                        1;
                                                return (
                                                    <Th
                                                        key={header.id}
                                                        colSpan={header.colSpan}
                                                        textTransform="capitalize"
                                                        fontSize="sm"
                                                        letterSpacing="wide"
                                                        paddingTop="12px"
                                                        paddingBottom="12px"
                                                        {...(isFirst && {
                                                            pl: 4,
                                                        })}
                                                        {...(isLast && {
                                                            pr: 0,
                                                        })}>
                                                        {flexRender(
                                                            header.column
                                                                .columnDef
                                                                .header,
                                                            header.getContext(),
                                                        )}
                                                    </Th>
                                                );
                                            },
                                        )}
                                    </Tr>
                                ))}
                            </Thead>

                            <Tbody>
                                {aggregateTable?.length > 0 ? (
                                    table.getRowModel().rows.map((row) => (
                                        <Tr
                                            key={row.id}
                                            bg={
                                                darkMode ? 'dark.tcap' : '#FFF'
                                            }>
                                            {row
                                                .getVisibleCells()
                                                .map((cell, cellIndex) => {
                                                    const isFirst =
                                                        cellIndex === 0;
                                                    const isLast =
                                                        cellIndex ===
                                                        row.getVisibleCells()
                                                            ?.length -
                                                            1;
                                                    return (
                                                        <Td
                                                            key={cell.id}
                                                            id={cell.id}
                                                            sx={{
                                                                border: 0,
                                                            }}
                                                            py={2}
                                                            {...(isFirst && {
                                                                pl: 4,
                                                            })}
                                                            {...(isLast && {
                                                                pr: 0,
                                                            })}>
                                                            <Box
                                                                display="flex"
                                                                alignItems="center"
                                                                justifyContent="flex-start">
                                                                {flexRender(
                                                                    cell.column
                                                                        .columnDef
                                                                        .cell,
                                                                    cell.getContext(),
                                                                )}
                                                            </Box>
                                                        </Td>
                                                    );
                                                })}
                                        </Tr>
                                    ))
                                ) : (
                                    <Tr>
                                        <Td
                                            colSpan={columns.length}
                                            className="!border-none !bg-transparent text-center">
                                            No data to display
                                        </Td>
                                    </Tr>
                                )}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </CardBody>
            </Card>
        </Box>
    );
}

export default AddInvoiceLineTable;
